'use client';

import Heading from '@atoms/Heading/Heading';
import Link from '@atoms/Link/Link';
import Icon from '@atoms/Icon/Icon';
import Text from '@atoms/Text/Text';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { trackClickInStoreDetail } from '@helpers/analyticsHelpers/trackStoreFinder';
import { useAppSelector } from '@hooks/useAppDispatch';
import { selectSideNavMenuIsOpen } from '@slices/menuSlice';
import { selectMiniCartPreviewIsOpen } from '@slices/miniCartSlice';
import Button from '@atoms/Button/Button';
import { StyledContentWrapper, StyledItemWrapper, StyledLink, StyledFlyerButton } from './StoreInfoComponent.styles';
import Offer from '@icons/offer.svg';
import { extractStoreId } from '@helpers/storeHelper';
import useCmsComponents from '@hooks/useCmsComponents';
import { useParams } from 'next/navigation';
import useFeatureToggle from '@hooks/useFeatureToggle';
import Config from '@config';
import useStoreFlyerData from '@hooks/useStoreFlyerData';
import StoreFlyerModal from '@molecules/PromotionTabs/StoreFlyerModal/StoreFlyerModal';

const defaultAmountOfFeatures = 6;

type StoreInfoComponentProps = {
  data: StoreInfoComponentType;
};

const hiddenFeatures = [
  'StoreFeatureId-0000000100', // Burek
  'StoreFeatureId-0000000059', // Picadeli
  'StoreFeatureId-0000000049', // Färskt bröd
  'StoreFeatureId-0000000101', // Grill
];

const sortSpecialOpeningDays = (
  specialOpeningDays: StoreInfoComponentDataOpeningHoursType
): Array<StoreInfoComponentDataSpecialOpeningDay> => {
  // Sort keys numerically:
  return Object.entries(specialOpeningDays)
    .sort(([key1], [key2]) => parseInt(key1.replace(/\D/g, '')) - parseInt(key2.replace(/\D/g, '')))
    .map(([, value]) => value);
};

const DAY_KEYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;
type DayKey = (typeof DAY_KEYS)[number];

const isDayKey = (value: string): value is DayKey => DAY_KEYS.includes(value as DayKey);

const StoreInfoComponent = ({ data }: StoreInfoComponentProps) => {
  const sideMenuIsOpen = useAppSelector(selectSideNavMenuIsOpen);
  const isCartPreviewOpen = useAppSelector(selectMiniCartPreviewIsOpen);
  const [showOpeningHours, setShowOpeningHours] = useState(false);
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const [isFlyerModalOpen, setIsFlyerModalOpen] = useState(false);
  const params = useParams();
  const t = useTranslations('storePage');
  const tDay = useTranslations('storePage.day');

  const translateDay = (value: string) => (isDayKey(value) ? tDay(value) : value);

  const storeId = extractStoreId(params?.storeParameters || '');
  const { data: storeInfoComponents } = useCmsComponents(JSON.stringify(data.uid), storeId);
  const storeInfoData = storeInfoComponents?.component?.filter(
    (component): component is StoreInfoComponentDataType => 'store' in component
  )?.[0];
  const amountOfFeatures = Object.keys(storeInfoData?.storeFeatures || []).length;

  const { isEnabled: isNextWeekFlyerEnabled } = useFeatureToggle(Config.FEATURE_TOGGLES.nextWeekFlyer);
  const { storeFlyerData } = useStoreFlyerData(isNextWeekFlyerEnabled ? storeId : undefined);

  return (
    <>
      <StyledItemWrapper $gap={20}>
        <Heading variant="h3">{t('storeInfo')}</Heading>
        {storeInfoData?.store?.flyerURL && storeInfoData.isOpen === 'true' && (
          <Text type="body">
            {isNextWeekFlyerEnabled && storeFlyerData ? (
              <StyledFlyerButton
                variant="link"
                onClick={() => {
                  trackClickInStoreDetail(t('showFlyer'));
                  setIsFlyerModalOpen(true);
                }}
              >
                <Icon svg={Offer} size={20} color="highlight" /> {t('showFlyer')}
              </StyledFlyerButton>
            ) : (
              <StyledLink
                href={storeInfoData.store.flyerURL}
                onClick={() => {
                  trackClickInStoreDetail(t('showFlyer'));
                }}
                target="_blank"
              >
                <Icon svg={Offer} size={20} color="highlight" /> {t('showFlyer')}
              </StyledLink>
            )}
          </Text>
        )}
      </StyledItemWrapper>
      <StyledContentWrapper $menuCartOpen={sideMenuIsOpen && isCartPreviewOpen} $cartOpen={isCartPreviewOpen}>
        {storeInfoData && (
          <>
            {storeInfoData.mergedOpeningDays && (
              <StyledItemWrapper $gap={8}>
                <Text type="label" size="medium">
                  {t('openingHours')}
                </Text>

                <div>
                  {Object.entries(storeInfoData.mergedOpeningDays)
                    // Sort by day of week (MONDAY, SATURDAY, SUNDAY)
                    .sort(([key1], [key2]) => key1.localeCompare(key2))
                    .map(([key, value]) => {
                      const name1 = translateDay(value.name1);
                      const name2 = value.name2 ? translateDay(value.name2) : null;

                      return (
                        <Text key={key} type="body">
                          {name2 ? `${name1}-${name2}` : name1} {value.openingHours}-{value.closingHours}
                        </Text>
                      );
                    })}

                  {Object.keys(storeInfoData?.specialOpeningDays).length !== 0 && (
                    <Button
                      variant="link"
                      onClick={() => {
                        trackClickInStoreDetail(t('weekendOpeningHours'));
                        setShowOpeningHours(!showOpeningHours);
                      }}
                    >
                      <Text element="span" type="subhead" color="blue">
                        {t('weekendOpeningHours')} {showOpeningHours ? '-' : '+'}
                      </Text>
                    </Button>
                  )}

                  {showOpeningHours && (
                    <div>
                      {sortSpecialOpeningDays(storeInfoData.specialOpeningDays).map((value) => {
                        return (
                          <Text
                            key={value.name1}
                            type="body"
                          >{`${value.name1} ${value.openingHours}-${value.closingHours}`}</Text>
                        );
                      })}
                    </div>
                  )}
                </div>
              </StyledItemWrapper>
            )}

            <StyledItemWrapper $gap={8}>
              <Text type="label" size="medium">
                {t('findUs')}
              </Text>

              <>
                <Text type="body">{storeInfoData?.storeStreetName}</Text>
                <Text type="body">{`${storeInfoData?.storePostalCode} ${storeInfoData?.storeTown}`}</Text>

                <Text type="body">
                  <Link
                    href={`https://maps.google.se?saddr=current+location&daddr=Willys+${storeInfoData?.storeTown}+${storeInfoData?.storeStreetName}+${storeInfoData?.storePostalCode}`}
                    onClick={() => {
                      trackClickInStoreDetail(t('directions'));
                    }}
                    target="_blank"
                  >
                    {t('directions')}
                  </Link>
                </Text>

                <Text type="body">
                  <Link
                    href={`https://www.google.se/maps?q=Willys+${storeInfoData?.storeTown}+${storeInfoData?.storeStreetName}+${storeInfoData?.storePostalCode}`}
                    onClick={() => {
                      trackClickInStoreDetail(t('showOnMap'));
                    }}
                    target="_blank"
                  >
                    {t('showOnMap')}
                  </Link>
                </Text>
              </>
            </StyledItemWrapper>

            <StyledItemWrapper $gap={8}>
              <Text type="label" size="medium">
                {t('contact')}
              </Text>
              <Text type="body">
                <a href={`tel:${storeInfoData?.storePhone}`}>{storeInfoData?.storePhone}</a>
              </Text>
            </StyledItemWrapper>

            {storeInfoData.storeFeatures && (
              <StyledItemWrapper $gap={8}>
                <Text type="label" size="medium">
                  {t('service')}
                </Text>

                <div>
                  {Object.entries(storeInfoData.storeFeatures).map(([key, feature], index) => {
                    if (hiddenFeatures.includes(key) || (!showAllFeatures && index >= defaultAmountOfFeatures)) {
                      return null;
                    }
                    return (
                      <Text type="body" key={index}>
                        {feature}
                      </Text>
                    );
                  })}

                  {amountOfFeatures > defaultAmountOfFeatures && !showAllFeatures && (
                    <Button variant="link" onClick={() => setShowAllFeatures(true)}>
                      <Text element="span" type="subhead" color="blue">
                        {t('showAll')}
                      </Text>
                    </Button>
                  )}
                </div>
              </StyledItemWrapper>
            )}
          </>
        )}
      </StyledContentWrapper>
      {isFlyerModalOpen && storeFlyerData && storeId && (
        <StoreFlyerModal data={storeFlyerData} onClose={() => setIsFlyerModalOpen(false)} />
      )}
    </>
  );
};

export default StoreInfoComponent;
